"use client"

import { cn } from "@/components/common"
import { getProductPrice } from "@lib/util/get-product-price"
import { HttpTypes } from "@medusajs/types"
import { useTranslations } from "next-intl"
import type { RentalInfo } from "@/types/rental"
import { UnifiedPriceDisplay } from "../unified-price-display"

export default function ProductPrice({
  product,
  variant,
  rentalInfo,
}: {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
  rentalInfo?: RentalInfo | null
}) {
  const t = useTranslations('product')

  const { cheapestPrice, variantPrice } = getProductPrice({
    product,
    variantId: variant?.id,
  })

  const selectedPrice = variant ? variantPrice : cheapestPrice

  // Loading state
  if (!selectedPrice && !rentalInfo?.isRentalAvailable) {
    return <div className="block w-32 h-9 bg-muted animate-pulse" />
  }

  // Get currency code from selected price or rental info
  const currencyCode = selectedPrice?.currency_code || rentalInfo?.currencyCode || 'CZK'

  return (
    <div className="flex flex-col gap-1 text-ui-fg-base">
      {/* Unified price display */}
      <UnifiedPriceDisplay
        rentalInfo={rentalInfo}
        purchasePrice={selectedPrice?.calculated_price}
        currencyCode={currencyCode}
        variant="default"
      />

      {/* Sale indicator */}
      {selectedPrice?.price_type === "sale" && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-ui-fg-subtle">{t('originalPrice')}</span>
          <span
            className="line-through"
            data-testid="original-product-price"
            data-value={selectedPrice.original_price_number}
          >
            {selectedPrice.original_price}
          </span>
          <span className="text-ui-fg-interactive">
            -{selectedPrice.percentage_diff}%
          </span>
        </div>
      )}
    </div>
  )
}
